/**
 * Sekce do ModelDetailPage, které přidávají data z PIM:
 *   - `<CompatiblePowerpartsSection>` — PowerParts/PowerWear kompatibilní
 *     s aktuálním modelem, seskupené podle kategorie.
 *   - `<ModelAssetsSection>` — PDF dokumenty a marketingové obrázky k modelu.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getModelPowerparts, getModelAssets } from "../lib/api.js";
import type {
  ModelPowerpartsResponse,
  ModelPowerpartGroup,
  ModelPowerpartItem,
  ModelAssetsResponse,
  ModelDocument,
  ModelCompatibleDocument,
  ModelAsset,
} from "../lib/types.js";
import { PriceDisplay } from "../ui/PriceDisplay.js";
import { Badge } from "../ui/Badge.js";
import { PackageIcon, DocumentIcon, ChevronRightIcon } from "../lib/icons.js";

/**
 * Hook, který centralizuje fetch PowerParts pro daný model.
 * Sdílený mezi navigací (podskupiny v sidebaru) a hlavní sekcí obsahu —
 * jeden fetch, dvě použití.
 */
export function useModelPowerparts(modelId: string): {
  data: ModelPowerpartsResponse | null;
  loading: boolean;
} {
  const [data, setData] = useState<ModelPowerpartsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    getModelPowerparts(modelId)
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  return { data, loading };
}

/** Stabilní slug z group.key pro použití jako DOM id / URL kotva. */
export function powerpartsGroupAnchor(groupKey: string): string {
  return "group-__pp-" + groupKey
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// =============================================================================
// CompatiblePowerpartsSection
// =============================================================================

export function CompatiblePowerpartsSection({
  data,
  loading,
  onEnterView,
  onEnterSubgroup,
}: {
  data: ModelPowerpartsResponse | null;
  loading: boolean;
  onEnterView?: () => void;
  /** Volá se když se libovolná subkategorie dostane do viewportu (pro active state v navu). */
  onEnterSubgroup?: (anchorId: string) => void;
}): JSX.Element | null {
  // Pokud nic není kompatibilní, sekci schováme (nechceme prázdný blok).
  if (!loading && (!data || data.totalCompatibleArticles === 0)) return null;

  return (
    <section
      id="group-__powerparts"
      className="scroll-mt-24"
      ref={(el) => {
        if (!el || !onEnterView) return;
        const obs = new IntersectionObserver(
          ([entry]) => entry?.isIntersecting && onEnterView(),
          { rootMargin: "-30% 0px -60% 0px" },
        );
        obs.observe(el);
        return () => obs.disconnect();
      }}
    >
      <header className="mb-4 flex items-baseline justify-between">
        <div>
          <h2 className="text-lg font-semibold uppercase tracking-tight text-zinc-900">
            PowerParts a příslušenství
          </h2>
          {data && data.totalCompatibleArticles > 0 && (
            <p className="mt-1 text-sm text-zinc-500">
              {data.groups.length}{" "}
              {pluralCs(data.groups.length, ["skupina", "skupiny", "skupin"])} ·{" "}
              {data.totalCompatibleArticles}{" "}
              {pluralCs(data.totalCompatibleArticles, [
                "položka",
                "položky",
                "položek",
              ])}
            </p>
          )}
        </div>
      </header>

      {loading ? (
        <div className="text-sm text-zinc-500">Načítám kompatibilní PowerParts…</div>
      ) : (
        <div className="space-y-8">
          {data!.groups.map((g) => (
            <PowerpartsGroup
              key={g.key}
              group={g}
              {...(onEnterSubgroup ? { onEnterView: onEnterSubgroup } : {})}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function PowerpartsGroup({
  group,
  onEnterView,
}: {
  group: ModelPowerpartGroup;
  onEnterView?: (anchorId: string) => void;
}): JSX.Element {
  const anchor = powerpartsGroupAnchor(group.key);
  return (
    <div
      id={anchor}
      className="scroll-mt-24"
      ref={(el) => {
        if (!el || !onEnterView) return;
        const obs = new IntersectionObserver(
          ([entry]) => entry?.isIntersecting && onEnterView(anchor),
          { rootMargin: "-30% 0px -60% 0px" },
        );
        obs.observe(el);
        return () => obs.disconnect();
      }}
    >
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-700 flex items-center gap-2">
        <span>{group.hierarchyL3 ?? categoryLabel(group.pimCategory)}</span>
        <Badge tone={categoryTone(group.pimCategory)}>
          {categoryLabel(group.pimCategory)}
        </Badge>
        <span className="text-xs font-normal text-zinc-400">
          {group.items.length}
        </span>
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {group.items.map((it) => (
          <CompatibleCard key={it.id} item={it} />
        ))}
      </div>
    </div>
  );
}

function CompatibleCard({ item }: { item: ModelPowerpartItem }): JSX.Element {
  const isDiscontinued = item.articleStatus === "80";
  const hasStock = item.physicalSellable > 0;
  // Primární sklad = první s qty > 0 (typicky Vlašim)
  const primaryWarehouse = item.warehouses[0] ?? null;
  // Sklad u výrobce: zobrazujeme jen když má konkrétní číslo >0, nebo status
  // "available/manufacturerAvailable". ETA je zajímavá i pro "onOrder".
  const mfgStock = item.manufacturerStock ?? 0;
  const mfgAvail = item.manufacturerAvailability;
  const hasMfgStock = mfgStock > 0;
  const isMfgAvailable =
    mfgAvail === "available" || mfgAvail === "manufacturerAvailable";
  const showMfgBadge = hasMfgStock || isMfgAvailable;

  return (
    <Link
      to={`/part/${encodeURIComponent(item.articleNumber)}`}
      className="group bg-white rounded-xl border border-zinc-200 overflow-hidden hover:border-ktm-orange hover:shadow-md transition-all"
    >
      <div className="aspect-square bg-zinc-50 relative overflow-hidden">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name ?? ""}
            className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300">
            <PackageIcon size={40} />
          </div>
        )}
        {isDiscontinued && (
          <div className="absolute top-2 right-2">
            <Badge tone="red">Zrušeno</Badge>
          </div>
        )}
        {/* Badge: skladem ve Vlašimi (dealer) — zelený */}
        {hasStock && primaryWarehouse && (
          <div
            className="absolute bottom-2 left-2 inline-flex items-center gap-1 bg-white/95 backdrop-blur px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 text-[11px] font-semibold"
            title={item.warehouses
              .map((w) => `${w.name}: ${w.qtyAvailable} skladem, ${w.qtyInbound} v cestě`)
              .join("\n")}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {item.physicalSellable} ks{" "}
            {primaryWarehouse.name.replace(/KTM |dealer.*|\(.*\)/gi, "").trim() ||
              primaryWarehouse.code}
          </div>
        )}
        {/* Badge: skladem u výrobce (KTM AG) — modrý, vpravo dole */}
        {showMfgBadge && (
          <div
            className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-white/95 backdrop-blur px-2 py-1 rounded-md border border-sky-200 text-sky-700 text-[11px] font-semibold"
            title={
              item.manufacturerEtaDate
                ? `KTM AG: ${mfgAvailLabel(mfgAvail)} · ETA ${item.manufacturerEtaDate.slice(0, 10)}`
                : `KTM AG: ${mfgAvailLabel(mfgAvail)}`
            }
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
            {hasMfgStock ? `${mfgStock} ks KTM AG` : "KTM AG"}
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-xs text-zinc-400 font-mono">{item.articleNumber}</div>
        <div className="mt-1 text-sm font-medium text-zinc-900 line-clamp-2 group-hover:text-ktm-orange transition-colors min-h-[2.5rem]">
          {item.name ?? "Bez názvu"}
        </div>
        <div className="mt-2 flex items-center justify-between">
          {item.price ? (
            <PriceDisplay price={item.price} size="sm" />
          ) : (
            <span className="text-xs text-zinc-400">Bez ceny</span>
          )}
          <ChevronRightIcon size={14} className="text-zinc-300 group-hover:text-ktm-orange transition-colors" />
        </div>
      </div>
    </Link>
  );
}

// =============================================================================
// ModelAssetsSection
// =============================================================================

export function ModelAssetsSection({
  modelId,
  onEnterView,
}: {
  modelId: string;
  onEnterView?: () => void;
}): JSX.Element | null {
  const [data, setData] = useState<ModelAssetsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getModelAssets(modelId)
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  const totalDocs = data
    ? data.directDocuments.length + data.compatibleDocuments.length
    : 0;
  const hasContent = data && (totalDocs > 0 || data.media.length > 0);
  if (!loading && !hasContent) return null;

  return (
    <section
      id="group-__assets"
      className="scroll-mt-24"
      ref={(el) => {
        if (!el || !onEnterView) return;
        const obs = new IntersectionObserver(
          ([entry]) => entry?.isIntersecting && onEnterView(),
          { rootMargin: "-30% 0px -60% 0px" },
        );
        obs.observe(el);
        return () => obs.disconnect();
      }}
    >
      <header className="mb-4">
        <h2 className="text-lg font-semibold uppercase tracking-tight text-zinc-900">
          Dokumenty a podklady
        </h2>
        {data && (
          <p className="mt-1 text-sm text-zinc-500">
            {totalDocs}{" "}
            {pluralCs(totalDocs, ["dokument", "dokumenty", "dokumentů"])} ·{" "}
            {data.media.length}{" "}
            {pluralCs(data.media.length, ["obrázek", "obrázky", "obrázků"])}
          </p>
        )}
      </header>

      {loading ? (
        <div className="text-sm text-zinc-500">Načítám podklady…</div>
      ) : (
        <div className="space-y-8">
          {data && data.compatibleDocuments.length > 0 && (
            <CompatibleDocumentsList documents={data.compatibleDocuments} />
          )}
          {data && data.directDocuments.length > 0 && (
            <DirectDocumentsList documents={data.directDocuments} />
          )}
          {data && data.media.length > 0 && (
            <MediaGallery media={data.media} />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Dokumenty z kompatibilních PP/PW (= balíček k této motorce).
 * Seskupené per documentType, pod každým typem (např. "Návod k použití") jsou
 * PDF listnuté s odkazem na zdrojový díl (aby user věděl o čem PDF je).
 */
function CompatibleDocumentsList({
  documents,
}: {
  documents: ModelCompatibleDocument[];
}): JSX.Element {
  const byType = new Map<string, ModelCompatibleDocument[]>();
  for (const d of documents) {
    const existing = byType.get(d.documentType);
    if (existing) existing.push(d);
    else byType.set(d.documentType, [d]);
  }

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-700">
        Z kompatibilních dílů{" "}
        <span className="font-normal text-zinc-400">{documents.length}</span>
      </h3>
      <div className="space-y-4">
        {Array.from(byType.entries()).map(([type, docs]) => (
          <div key={type}>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
              {documentTypeLabel(type)}{" "}
              <span className="font-normal text-zinc-400">{docs.length}</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {docs.map((d) => (
                <CompatibleDocumentLink key={d.id} doc={d} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompatibleDocumentLink({
  doc,
}: {
  doc: ModelCompatibleDocument;
}): JSX.Element {
  if (!doc.url) return <span className="text-xs text-zinc-400">Nedostupné</span>;
  return (
    <a
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 bg-white hover:border-ktm-orange hover:shadow-sm transition-all group"
    >
      <div className="w-10 h-10 rounded bg-zinc-100 flex items-center justify-center text-zinc-500 group-hover:text-ktm-orange group-hover:bg-orange-50 transition-colors shrink-0">
        <DocumentIcon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-zinc-900 truncate">
          {doc.sourceArticleName ?? doc.sourceArticleNumber ?? documentTypeLabel(doc.documentType)}
        </div>
        <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
          {doc.sourceArticleNumber && (
            <span className="font-mono">{doc.sourceArticleNumber}</span>
          )}
          {doc.language && (
            <>
              {doc.sourceArticleNumber && <span className="text-zinc-300">·</span>}
              <span>{doc.language}</span>
            </>
          )}
          {doc.sizeBytes != null && (
            <>
              <span className="text-zinc-300">·</span>
              <span>{formatBytes(doc.sizeBytes)}</span>
            </>
          )}
          {doc.partsUsingThis > 1 && (
            <>
              <span className="text-zinc-300">·</span>
              <span className="text-amber-700">sdíleno {doc.partsUsingThis}× díly</span>
            </>
          )}
        </div>
      </div>
    </a>
  );
}

/** Přímé dokumenty připojené přímo k bike-article (v PIM obvykle 0). */
function DirectDocumentsList({
  documents,
}: {
  documents: ModelDocument[];
}): JSX.Element {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-700">
        Přímo k motorce{" "}
        <span className="font-normal text-zinc-400">{documents.length}</span>
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {documents.map((d) => (
          <DocumentLink key={d.id} doc={d} />
        ))}
      </div>
    </div>
  );
}

function DocumentLink({
  doc,
  customLabel,
}: {
  doc: ModelDocument;
  customLabel?: string;
}): JSX.Element {
  if (!doc.url) return <span className="text-xs text-zinc-400">Nedostupné</span>;
  return (
    <a
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 bg-white hover:border-ktm-orange hover:shadow-sm transition-all group"
    >
      <div className="w-10 h-10 rounded bg-zinc-100 flex items-center justify-center text-zinc-500 group-hover:text-ktm-orange group-hover:bg-orange-50 transition-colors shrink-0">
        <DocumentIcon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-zinc-900 truncate">
          {customLabel ?? documentTypeLabel(doc.documentType)}
          {doc.version && <span className="text-xs text-zinc-400 ml-1">{doc.version}</span>}
        </div>
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          {doc.language && <span>{doc.language}</span>}
          {doc.sizeBytes != null && (
            <>
              {doc.language && <span className="text-zinc-300">·</span>}
              <span>{formatBytes(doc.sizeBytes)}</span>
            </>
          )}
        </div>
      </div>
    </a>
  );
}

function MediaGallery({ media }: { media: ModelAsset[] }): JSX.Element {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-700">
        Obrazové podklady{" "}
        <span className="text-xs font-normal text-zinc-400">{media.length}</span>
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {media.map(
          (m) =>
            m.url && (
              <a
                key={m.id}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                className="aspect-square bg-zinc-50 rounded-lg overflow-hidden border border-zinc-200 hover:border-ktm-orange transition-colors group"
              >
                <img
                  src={m.url}
                  alt={m.purpose ?? ""}
                  className="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform"
                />
              </a>
            ),
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function categoryLabel(c: string): string {
  return (
    {
      powerparts: "PowerParts",
      powerwear: "PowerWear",
      powerwear_collection: "PW Collection",
      plastic_kit: "Plastic Kit",
      wp_aftermarket: "WP Aftermarket",
      sparepart: "Náhradní díl",
      bike: "Motorka",
      other: "Ostatní",
    }[c] ?? c
  );
}

function categoryTone(c: string): "orange" | "blue" | "green" | "neutral" {
  return (
    (
      {
        powerparts: "orange",
        powerwear: "blue",
        powerwear_collection: "blue",
        plastic_kit: "orange",
        wp_aftermarket: "green",
      } as const
    )[c] ?? "neutral"
  );
}

function mfgAvailLabel(a: string | null): string {
  return (
    {
      available: "skladem",
      manufacturerAvailable: "skladem",
      onOrder: "na objednávku",
      notAvailable: "nedostupné",
      unknown: "neznámá dostupnost",
    }[a ?? ""] ?? "neznámá dostupnost"
  );
}

function documentTypeLabel(t: string): string {
  return (
    {
      BED: "Návod k použití",
      FACTSHEET: "Factsheet",
      GEN: "Obecný dokument",
      GENABE: "Allgemeine Betriebserlaubnis (TP)",
      GENEC: "ES prohlášení o shodě",
      GENECE: "Homologace ECE",
      GENECV: "Homologace ECV",
      GENKAT: "Marketingový leták",
      PDF: "PDF dokument",
      CAT: "Marketingový leták",
      HELMSIZE: "Velikostní tabulka přilby",
      PROTECTMAT: "Ochranné materiály",
      PROD: "Produktový list",
      SL: "Specifikace",
    }[t] ?? t
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function pluralCs(n: number, forms: [string, string, string]): string {
  if (n === 1) return forms[0];
  if (n >= 2 && n <= 4) return forms[1];
  return forms[2];
}
